import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@ludus/ui'
import type { Pipeline, PipelineId } from '@ludus/core'
import { deletePipeline, PipelineApiError, type RemovalBlocker } from '@/lib/api/pipelines.ts'
import { apiErrorMessage } from '@/shared/feedback/apiErrorMessage.ts'
import { blockerLabel } from './blockers.ts'

interface DeletePipelineDialogProps {
  open: boolean
  onOpenChange: (next: boolean) => void
  pipeline: Pipeline
  onDeleted: () => Promise<unknown>
}

/**
 * Deletion asks for the board's name typed back. The gesture is not ceremony:
 * every other destructive action in this console is reversible or migrates what
 * it touches, and this one is neither, so the confirmation has to cost more
 * than the reflex it guards against.
 *
 * The engine is asked again on submit and can still refuse — the report the
 * section read is a moment old, and a card that arrived since is exactly the
 * case where "delete" must turn back into "archive".
 */
export function DeletePipelineDialog({
  open,
  onOpenChange,
  pipeline,
  onDeleted,
}: DeletePipelineDialogProps) {
  const { t } = useTranslation()
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<PipelineApiError | null>(null)
  const [refused, setRefused] = useState<RemovalBlocker[] | null>(null)

  const confirmed = typed.trim() === pipeline.name.trim()

  const remove = async () => {
    if (busy || !confirmed) return
    setBusy(true)
    setError(null)
    setRefused(null)
    try {
      const result = await deletePipeline(pipeline.id as PipelineId)
      if (!result.deleted) {
        setRefused(result.blockers)
        return
      }
      await onDeleted()
      onOpenChange(false)
    } catch (err) {
      setError(err instanceof PipelineApiError ? err : null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('pages.pipelines.removal.dialog.title', {
              name: pipeline.name,
              defaultValue: 'Delete “{{name}}” permanently',
            })}
          </DialogTitle>
          <DialogDescription>
            {t('pages.pipelines.removal.dialog.description', {
              defaultValue:
                'Its stages, its source connection and its run history go with it. This cannot be undone.',
            })}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          <label className="flex flex-col gap-1.5 text-xs text-muted">
            {t('pages.pipelines.removal.dialog.prompt', {
              name: pipeline.name,
              defaultValue: 'Type {{name}} to confirm.',
            })}
            <Input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              aria-label={t('pages.pipelines.removal.dialog.inputLabel', {
                defaultValue: 'Pipeline name',
              })}
            />
          </label>

          {refused !== null && (
            <div className="flex flex-col gap-1 rounded-md border border-danger/40 bg-danger/5 p-2.5 text-xs text-danger">
              <span>
                {t('pages.pipelines.removal.dialog.refused', {
                  defaultValue: 'The engine refused: something now stands in the way.',
                })}
              </span>
              <ul className="flex list-disc flex-col gap-0.5 pl-4">
                {refused.map((blocker) => (
                  <li key={blocker.kind}>{blockerLabel(t, blocker)}</li>
                ))}
              </ul>
            </div>
          )}

          {error !== null && (
            <p className="text-xs text-danger">
              {apiErrorMessage(error, t, 'pages.pipelines.removal.dialog')}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={busy}
          >
            {t('pages.pipelines.removal.dialog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" size="sm" onClick={remove} disabled={!confirmed || busy}>
            {t('pages.pipelines.removal.dialog.confirm', { defaultValue: 'Delete permanently' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
